import { useEffect, useRef, useState } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { useUserProfile } from '../../../context/UserProfileContext';
import { AddressSearch, type SelectedPlace } from '../../../components/ui/AddressSearch';
import type { Organization } from '../../../types/admin';

// ── Color utilities ──────────────────────────────────────────────────────

const DEFAULT_BRAND_PRIMARY = '#6B2D8B';

function isValidHex(hex: string): boolean {
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex);
}

// ── Types ────────────────────────────────────────────────────────────────

export interface CompanyProfileData {
  displayName: string;
  legalName: string;
  billingEmail: string;
  phone: string;
  website: string;
  businessAddress: { address: string; placeId: string } | null;
  brandPrimaryColor: string;
  logoBase64: string | null;
  /** Footer / terms text printed on every client quote PDF and online quote. */
  quoteFooter: string;
}

interface CompanyProfileStepProps {
  org: Organization;
  onChange: (data: CompanyProfileData) => void;
}

// ── Component ────────────────────────────────────────────────────────────

export function CompanyProfileStep({ org, onChange }: CompanyProfileStepProps) {
  const { currentUser } = useUserProfile();
  const fileRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState(org.name || '');
  const [legalName, setLegalName] = useState(org.legalName || org.name || '');
  const [billingEmail, setBillingEmail] = useState(org.billingEmail || currentUser?.email || '');
  const [phone, setPhone] = useState(org.phone || '');
  const [website, setWebsite] = useState(org.website || '');
  const [businessAddress, setBusinessAddress] = useState<{ address: string; placeId: string } | null>(
    org.businessAddress ?? null,
  );
  const [brandColor, setBrandColor] = useState(
    org.bookingConfig?.brandPrimaryColor || DEFAULT_BRAND_PRIMARY,
  );
  const [logoBase64, setLogoBase64] = useState<string | null>(org.logoBase64 ?? null);
  const [logoError, setLogoError] = useState('');
  const [quoteFooter, setQuoteFooter] = useState(org.quoteFooter ?? '');

  // Notify parent of changes
  useEffect(() => {
    onChange({
      displayName,
      legalName,
      billingEmail,
      phone,
      website,
      businessAddress,
      brandPrimaryColor: brandColor,
      logoBase64,
      quoteFooter,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayName, legalName, billingEmail, phone, website, businessAddress, brandColor, logoBase64, quoteFooter]);

  function handleLogoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('That image is over 2 MB. Choose a smaller file (PNG or JPG, under 2 MB).');
      return;
    }
    setLogoError('');
    const reader = new FileReader();
    reader.onload = () => setLogoBase64(reader.result as string);
    reader.readAsDataURL(file);
  }

  function handleAddressChange(place: SelectedPlace | null) {
    setBusinessAddress(place ? { address: place.address, placeId: place.placeId } : null);
  }

  return (
    <div className="space-y-5">
      {/* Logo + Brand Color — side by side */}
      <div className="flex items-start gap-5">
        <div className="flex-1">
          <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Company Logo</label>
          <div className="flex items-center gap-4">
            {logoBase64 ? (
              <img
                src={logoBase64}
                alt="Logo"
                className="w-16 h-16 rounded-xl object-contain border border-stone bg-mist"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl border-2 border-dashed border-stone/60 flex items-center justify-center bg-mist">
                <Upload className="w-5 h-5 text-slate/70" />
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="px-3 py-2 text-xs font-medium text-slate bg-mist hover:bg-stone/30 border border-stone rounded-lg transition-colors"
              >
                {logoBase64 ? 'Change' : 'Upload'}
              </button>
              {logoBase64 && (
                <button
                  type="button"
                  onClick={() => setLogoBase64(null)}
                  className="px-3 py-2 text-xs font-medium text-red-600 bg-mist hover:bg-red-50 border border-stone rounded-lg transition-colors"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoFile} className="hidden" />
          </div>
          {logoError && <p className="text-[12px] text-rose mt-2">{logoError}</p>}
        </div>

        <div className="shrink-0">
          <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Brand Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={isValidHex(brandColor) ? brandColor : DEFAULT_BRAND_PRIMARY}
              onChange={(e) => setBrandColor(e.target.value.toUpperCase())}
              className="w-10 h-10 rounded-lg border border-stone cursor-pointer p-0.5"
            />
            <input
              type="text"
              value={brandColor}
              onChange={(e) => {
                let hex = e.target.value;
                if (!hex.startsWith('#')) hex = '#' + hex;
                setBrandColor(hex);
              }}
              maxLength={7}
              className="w-24 px-2 py-2 text-sm text-midnight bg-white border border-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-plum/20 font-mono"
              placeholder="#000000"
            />
          </div>
          <p className="text-[10px] text-slate mt-1">Customer-facing pages</p>
        </div>
      </div>

      {/* Company Display Name */}
      <div>
        <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
          Company Display Name
        </label>
        <p className="text-xs text-slate mb-2">
          How your company name appears to customers — on quotes, emails, and your booking page.
        </p>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. Acme Moving"
          className="w-full h-11 px-3 text-small text-midnight bg-white border border-stone rounded-lg
                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                     placeholder:text-slate transition-colors"
        />
      </div>

      {/* Legal Name */}
      <div>
        <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
          Legal Name <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-slate mb-2">
          Your company's registered legal name — used on contracts and official documents.
        </p>
        <input
          type="text"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          placeholder="e.g. Acme Moving Co., LLC"
          className="w-full h-11 px-3 text-small text-midnight bg-white border border-stone rounded-lg
                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                     placeholder:text-slate transition-colors"
        />
      </div>

      {/* Business Email */}
      <div>
        <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
          Business Email <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-slate/70 mb-2">
          Displayed on quotes as the contact email for your company.
        </p>
        <input
          type="email"
          value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
          placeholder="info@yourcompany.com"
          className="w-full px-3 py-2.5 text-sm text-midnight bg-white border border-stone rounded-xl focus:outline-none focus:ring-2 focus:ring-plum/20 placeholder:text-slate/70"
        />
      </div>

      {/* Phone */}
      <div>
        <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Phone Number</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(555) 123-4567"
          className="w-full px-3 py-2.5 text-sm text-midnight bg-white border border-stone rounded-xl focus:outline-none focus:ring-2 focus:ring-plum/20 placeholder:text-slate/70"
        />
      </div>

      {/* Website */}
      <div>
        <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Website</label>
        <input
          type="url"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://yourcompany.com"
          className="w-full px-3 py-2.5 text-sm text-midnight bg-white border border-stone rounded-xl focus:outline-none focus:ring-2 focus:ring-plum/20 placeholder:text-slate/70"
        />
      </div>

      {/* Business Address */}
      <div>
        <AddressSearch
          value={businessAddress}
          onChange={handleAddressChange}
          label="Business Address"
        />
      </div>

      {/* Quote footer / terms */}
      <div>
        <label htmlFor="setup-quote-footer" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
          Quote Footer / Terms
        </label>
        <p className="text-xs text-slate mb-2">
          Printed at the bottom of every client quote PDF and online quote: payment terms, cancellation policy, a thank-you.
          Otherwise quotes end with "Thank you for your business". Editable later under Customer-Facing → Online & PDF Quotes.
        </p>
        <textarea
          id="setup-quote-footer"
          value={quoteFooter}
          onChange={(e) => setQuoteFooter(e.target.value)}
          rows={3}
          maxLength={1000}
          placeholder="e.g. A 25% deposit reserves your move date. Balance due on completion. Cancellations within 48 hours forfeit the deposit."
          className="w-full px-3 py-2.5 text-sm text-midnight bg-white border border-stone rounded-xl focus:outline-none focus:ring-2 focus:ring-plum/20 placeholder:text-slate/70 resize-none"
        />
      </div>

    </div>
  );
}
